/**
 * Google Identity Services loader.
 *
 * GIS has to come from Google's own origin — the script is what talks to the
 * account session, and a self-hosted copy would not work even if it were
 * allowed. That makes it the one external dependency the sign-in screen has, so
 * a failure to load is reported rather than left as a page with no button.
 *
 * Types are declared narrowly here instead of pulling in a wrapper package: the
 * app uses three calls, and this is smaller than the dependency.
 */

/**
 * `hl=en` pins the language of Google's own button.
 *
 * The label is baked into the script bundle Google serves, and it picks the
 * language from the browser's UI language rather than from `navigator.language`
 * or from `renderButton`'s `locale` option — so a machine with a non-English
 * UI got an English page with a Filipino sign-in button. Pinning it here is
 * what actually decides. Revisit if the app is ever translated.
 */
const SCRIPT_SRC = 'https://accounts.google.com/gsi/client?hl=en'

export interface CredentialResponse {
  /** A signed Google ID token. Meaningless until the API verifies it. */
  credential?: string
  select_by?: string
}

interface IdConfiguration {
  client_id: string
  callback: (response: CredentialResponse) => void
  auto_select?: boolean
  cancel_on_tap_outside?: boolean
  /**
   * Filters the account chooser to one Workspace domain. A hint to the person
   * picking an account and nothing more — the API re-checks the domain against
   * the signed `hd` claim, because anything the browser sends can be changed.
   */
  hd?: string
  use_fedcm_for_prompt?: boolean
}

interface ButtonConfiguration {
  type?: 'standard' | 'icon'
  theme?: 'outline' | 'filled_blue' | 'filled_black'
  size?: 'small' | 'medium' | 'large'
  text?: 'signin_with' | 'signup_with' | 'continue_with' | 'signin'
  shape?: 'rectangular' | 'pill' | 'circle' | 'square'
  logo_alignment?: 'left' | 'center'
  width?: number
  locale?: string
}

export interface GoogleAccountsId {
  initialize: (config: IdConfiguration) => void
  renderButton: (parent: HTMLElement, config: ButtonConfiguration) => void
  disableAutoSelect: () => void
}

declare global {
  interface Window {
    google?: { accounts: { id: GoogleAccountsId } }
  }
}

export class GoogleUnavailableError extends Error {
  constructor() {
    super('Google sign-in could not be loaded')
    this.name = 'GoogleUnavailableError'
  }
}

/** Shared across callers so the script is requested once per page load. */
let pending: Promise<GoogleAccountsId> | null = null

export function loadGoogleIdentity(): Promise<GoogleAccountsId> {
  if (window.google?.accounts?.id) {
    return Promise.resolve(window.google.accounts.id)
  }

  pending ??= new Promise<GoogleAccountsId>((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(`script[src="${SCRIPT_SRC}"]`)
    const script = existing ?? document.createElement('script')

    script.addEventListener('load', () => {
      const api = window.google?.accounts?.id
      /**
       * A loaded script that did not define the API means something served a
       * response that was not GIS — a captive portal or a filtering proxy.
       * Treated as unavailable rather than trusted.
       */
      if (api) resolve(api)
      else reject(new GoogleUnavailableError())
    })

    script.addEventListener('error', () => {
      /** Let a later attempt retry rather than caching the failure forever. */
      pending = null
      reject(new GoogleUnavailableError())
    })

    if (!existing) {
      script.src = SCRIPT_SRC
      script.async = true
      script.defer = true
      document.head.append(script)
    }
  })

  return pending
}
